import React, { useEffect, useState, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux';

import _ from 'lodash';
import cx from 'classnames';
import { Frame } from "scenejs";

import { EditorContext } from '../../../../index';
import { useChildrenWidgetSelected, usePageWidgetSelector, useParentWidgetSelectorForType, useWidgetChildrenSelector, useWidgetSelected, useWidgetSelector } from 'components/Editor/hooks/useEditorSession';
import { CONTENT_FILTER, CONTENT_SEARCH } from 'constants/widgetTypes';
import { associateFontsWithWigdet } from 'actions/fonts';
import { InputGroup, Form, Button, Tab, Tabs } from 'react-bootstrap';
import { Property } from '../../../../components/properties';
import { AssetSearchBox } from '@paperflite-marketing/cleverstory-commons';
import { Font } from '../../../../components/properties/fonts';

import { toggleEditMode, unselectWidget, updateWidgetProperty } from 'actions/editor';
import DropShadow from 'components/Editor/components/properties/dropShadow';
import { calculateBoundsForElement, getParentContainerOfType } from 'components/Editor/helper/widget';

import { getMediaInfo } from 'actions/pageHealth';
import styles from './search.module.scss';

const IMAGE_CDN_ENDPOINT = process.env.REACT_APP_IMAGE_CDN;

const SearchWidget = React.memo(function SearchWidget({ className, style, widgetProps, widgetRef, children, parentProps, alignment}) {
  const dispatch = useDispatch();
  const ref = widgetRef;
  const columnRef = useRef(null);

  const editorContext = React.useContext(EditorContext);
  const editor = editorContext.editorSession.id;

  const [state, setState] = useState({
    columnStyles: {},
  });

  const breakpoint = useSelector(state => _.get(state, `${editor}.present.breakpoint`));
  const properties = _.get(widgetProps, 'properties', {});
  const deviceProperties = _.get(properties, `${breakpoint}`, {});
  const parentDeviceProperties = _.get(parentProps, `properties.${breakpoint}`, {});
  const id = _.get(widgetProps, 'uid');
  const contentWidgetId = _.get(parentProps, 'parent');
  const type = _.get(widgetProps, 'type');
  const parent = _.get(widgetProps, 'parent');
  const layout = _.get(widgetProps, `properties.${breakpoint}.layout`);

  const selected = useWidgetSelected(editor, id);
  const contentWidgetProps = useWidgetSelector(editor, contentWidgetId);
  const parentSelected = useWidgetSelected(editor, parent);
  const siblingSelection = useChildrenWidgetSelected(editor, parent);
  const searchColumnSelected = useSelector(state => {
    return _.get(state, `${editor}.present.editMode`) === id
  });
  const childrenSelection = useChildrenWidgetSelected(editor, id);

  const horizontalColumn = _.get(parentDeviceProperties, 'horizontalColumn', false);
  const stretched = _.get(contentWidgetProps, `properties.${breakpoint}.stretched`, false);
  const searchBoxSelectable = horizontalColumn ? searchColumnSelected : parentSelected;

  useEffect(() => {
    if (searchColumnSelected) {
      dispatch(unselectWidget(editor, parent))
    }
  }, [searchColumnSelected])

  const prevHorizontalColumn = useRef(horizontalColumn);

  useEffect(() => {
    if (breakpoint === 'desktop') {
      var request = {
        ...deviceProperties,
        layout: {
          ...layout,
          width: prevHorizontalColumn.current !== horizontalColumn ? '375px' : _.get(layout, 'width'),
          transform: {
            ...layout.transform,
            translate: "0px,0px"
          }
        }
      }
      dispatch(updateWidgetProperty(editor, id, breakpoint, request));
      prevHorizontalColumn.current = horizontalColumn;
    }
  }, [horizontalColumn])

  useEffect(() => {
    var searchColumnStyles = getColumnStyles(deviceProperties, horizontalColumn, breakpoint, alignment);
    setState(state => {
      return {
        ...state,
        columnStyles: searchColumnStyles,
      }
    })
  }, [horizontalColumn, deviceProperties, breakpoint, alignment]);

  const handleColumnSelection = () => {
    if (!horizontalColumn || searchColumnSelected) {
      return;
    }

    if (parentSelected && !selected) {
      dispatch(toggleEditMode(editor, id, true))
    }
  }

  useEffect(() => {
    const searchwidth = ref.current.clientWidth;
    if(!stretched && breakpoint == 'desktop') {
      var request = {
        ...deviceProperties,
        layout: {
          ...layout,
          width: searchwidth > 1200 ? '1200px': `${searchwidth}px`,
          transform: {
            ...layout.transform,
            translate: "0px,0px"
          }
        }
      }
      dispatch(updateWidgetProperty(editor, id, breakpoint, request));
    }
  }, [stretched]);

  useEffect(() => {
    const parentElement = document.querySelector(
      `#${editor} .editor_widget_scroll_container #search_container_column_${id}`
    );

    if (!parentElement) return;

    if (layout.width === '100%' && breakpoint === 'mobile') {
      parentElement.style.width = '100%';
    } else {
      // reset for desktop / other breakpoints
      parentElement.style.removeProperty('width');
    }
  }, [layout.width, breakpoint]);

  return (
    <div
      id={`search_container_column_${id}`}
      ref={columnRef}
      onClick={handleColumnSelection}
      style={(horizontalColumn || breakpoint === 'mobile') ? state.columnStyles : { }}
      className={cx({
        [styles.searchColumnWrapper]: horizontalColumn,
        [styles.selectable]: parentSelected || searchColumnSelected,
        [styles.selected]: searchColumnSelected
      })}
    >
      <SearchColumnAction
        editorSession={editor}
        breakpoint={breakpoint}
        show={(searchColumnSelected && !selected)}
        uid={id}
      />
      <div
        id={id}
        ref={ref}
        className={cx(styles.searchContainer, 'widget', {
          [styles.selected]: selected,
          [styles.parentSelected]: parentSelected,
          [styles.partialSelection]: childrenSelection.selected
        })}
        data-widget-id={id}
        style={style}
        data-widget-type={type}
        data-selectable={searchBoxSelectable || siblingSelection.selected}
      >
        <div className={styles.searchWrapper}>
          <AssetSearchBox
            className={styles.widgetWrapperStyle}
            widgetProps={widgetProps}
            breakpoint={breakpoint}
          />
        </div>
      </div>
    </div>
  )
})

const SearchColumnStyleEditor = ({ id, widgetProps, position, ...rest }) => {
  const dispatch = useDispatch();

  const editorContext = React.useContext(EditorContext);
  const editorSession = editorContext.editorSession.id;
  const breakpoint = useSelector(state => _.get(state, `${editorSession}.present.breakpoint`));
  const properties = _.get(widgetProps, `properties.${breakpoint}`, {});

  const alignment = _.get(properties, 'horizontalAlignment', {
    position: 'center',
    margin: breakpoint === 'desktop' ? 5 : 15
  });

  const handlePropertyChange = (value) => {
    dispatch(updateWidgetProperty(editorSession, id, breakpoint, value));
  }


  const maxHorizontalMargin = useSelector(state => {
    const widgets = _.get(state, `${editorSession}.present.widgets`) || [];
    let searchContainer = widgets.find(item => item.uid === id && item.type === CONTENT_SEARCH);
    const serachColumnElement = searchContainer ? document.getElementById(`search_container_column_${searchContainer.uid}`) : null;
    const searchElement = searchContainer ? document.getElementById(searchContainer.uid) : null;
    return serachColumnElement.clientWidth - searchElement.clientWidth;
  });

  const handleAlignmentChange = (value) => {
    const position = _.get(value, 'position');


    if (position === 'center') {
      value = {
        ...value,
        margin: 5
      }
    }

    var request = {
      ...properties,
      horizontalAlignment: {
        ...alignment,
        ...value
      }
    }

    handlePropertyChange(request);
  }

  return (
    <Property.Dialog className={styles.fontDialog}
      width={246}
      target={`#${id}`}
      title={'Horizontal Alignment'}
      onCancel={rest.onCancel}
    >
      <Property.Body>
        <Property nameStyle={styles.propertyName} className={styles.buttonAlignmentProperty}>
          <SearchContainerHorizontalAlignment
            value={alignment}
            breakpoint={breakpoint}
            maxHorizontalMargin={maxHorizontalMargin}
            onChange={handleAlignmentChange}
          />
        </Property>
      </Property.Body>
    </Property.Dialog>
  );
}

const SearchColumnAction = (props) => {
  const dispatch = useDispatch();

  const [view, setView] = useState(null);

  const editorContext = React.useContext(EditorContext);
  const editorSession = editorContext.editorSession.id;
  const id = _.get(props, 'uid');
  const widgetProps = useWidgetSelector(editorSession, id);

  const changeView = (view) => {
    setView(view);
  }

  const closePopup = () => {
    setView(null);
  }

  return (
    <>
      <div className={cx(styles.columnSelector, { [styles.showColumnAction]: props.show })}>
        <div className={styles.searchColumnAction}>
          <Property.Action className={styles.iconButton} onClick={(e) => changeView('style')}>
            <i className={cx("cleverstory-customize")} />
          </Property.Action>
          <Property.Divider />
          <Property.Action className={styles.iconButton} onClick={() => { window.open(`${process.env.REACT_APP_CLEVERSTORY_SUPPORT}`, '_blank') }}>
            <i className={'cleverstory-help'} />
          </Property.Action>
        </div>
        <label className={cx(styles.title)}>
          searchColumn
        </label>
      </div>

      {(view == 'style' && props.show) &&
        <SearchColumnStyleEditor
          id={id}
          editor={editorSession}
          widgetProps={widgetProps}
          onCancel={() => {
            closePopup()
          }}
        />
      }
    </>
  )
}


const SearchWidgetDesktopAction = (props) => {
  const dispatch = useDispatch();

  const [view, setView] = useState(null);

  const editorContext = React.useContext(EditorContext);
  const editorSession = editorContext.editorSession.id;

  // const contentWidget = useParentWidgetSelectorForType(editorSession, props.uid, CONTENT_SEARCH);

  const id = _.get(props, 'uid');
  const editMode = useSelector(state => {
    return _.get(state, `${editorSession}.present.editMode`) === id
  });

  const changeView = (view) => {
    dispatch(toggleEditMode(editorSession, props.uid, false));
    setView(view);
  }

  const closePopup = () => {
    dispatch(toggleEditMode(editorSession, props.uid, false));
    setView(null);
  }

  return (
    <>
      <Property.Action className={styles.iconButton} onClick={() => changeView('style')}>
        <i className={cx("cleverstory-customize")} />
      </Property.Action>
      <Property.Divider />
      <Property.Action className={styles.iconButton} onClick={() => { window.open(`${process.env.REACT_APP_CLEVERSTORY_SUPPORT}`, '_blank') }}>
        <i className={'cleverstory-help'} />
      </Property.Action>


      {!editMode && view === 'style' &&
        <SearchBoxStyleEditor
          id={id}
          editor={editorSession}
          properties={props}
          onCancel={() => {
            closePopup()
          }}
        />
      }
    </>
  )
}


const SearchContainerHorizontalAlignment = ({ value, breakpoint, maxHorizontalMargin, onChange }) => {

  const position = _.get(value, 'position') || 'center';
  const margin = _.get(value, 'margin', 0);


  const handleAlignmentChange = (position) => {
    var request = {
      position: position
    }

    if (position === 'right' || position === 'left') {
      request = {
        ...request,
        margin: breakpoint === 'desktop' ? 5 : 15
      }
    }

    onChange(request);
  }

  const handleMarginChange = (margin) => {
    var request = {
      margin: margin
    }

    onChange(request);
  }

  return (
    <div className={styles.buttonAlignment}>
      <span className={styles.subDialogName}>Alignment</span>
      <div className={styles.options}>
        <button className={cx(styles.alignment,
          {
            [styles.active]: position === 'left'
          })}
          onClick={handleAlignmentChange.bind(this, 'left')}
        >
          <i className={cx('cleverstory-widgets-align-left')}></i>
        </button>
        <button className={cx(styles.alignment,
          {
            [styles.active]: position === 'center'
          })}
          onClick={handleAlignmentChange.bind(this, 'center')}
        >
          <i className={cx('cleverstory-widgets-align-center')}></i>
        </button>
        <button className={cx(styles.alignment,
          {
            [styles.active]: position === 'right'
          })}
          onClick={handleAlignmentChange.bind(this, 'right')}
        >
          <i className={cx('cleverstory-widgets-align-right')}></i>
        </button>
      </div>


      {position !== 'center' &&
        <Property name="Margin" className={styles.marginProperty}>
          <Property.Slider
            input
            min={breakpoint === 'desktop' ? 5 : 15}
            max={maxHorizontalMargin}
            step={1}
            value={margin}
            onChange={handleMarginChange}
          />
        </Property>
      }
    </div>
  )
}

const SearchBoxStyleEditor = ({ id, properties, editor, ...rest }) => {
  const dispatch = useDispatch();

  const breakpoint = useSelector(state => _.get(state, `${editor}.present.breakpoint`));
  const deviceProperties = _.get(properties, `properties.${breakpoint}`, {});
  const border = _.get(deviceProperties, 'border', {});
  const borderWidth = _.get(border, 'width', 0);
  const borderColor = _.get(border, 'color');
  const borderStyle = _.get(border, 'style', 'all');
  const borderRadius = _.get(border, 'radius', {
    topLeft: 0,
    topRight: 0,
    bottomLeft: 0,
    bottomRight: 0
  });

  const handlePropertyChange = (value) => {
    dispatch(updateWidgetProperty(editor, id, breakpoint, value));
  }

  const handleBorderColor = (color) => {
    var request = deviceProperties
    _.set(request, `border.color`, color);
    handlePropertyChange(request);
  }

  const handleBorderStyle = (style) => {
    var request = deviceProperties
    _.set(request, `border.style`, style);
    handlePropertyChange(request);
  }

  const handleBorderWidth = (value) => {
    var request = deviceProperties
    _.set(request, `border.width`, value);
    handlePropertyChange(request);
  }

  const handleBorderRadius = (radius) => {
    var request = deviceProperties
    _.set(request, `border.radius`, radius);
    handlePropertyChange(request);
  }

  return (
    <Property.Dialog className={styles.customStylesDialog}
      width={256}
      target={`#${id}`}
      title={'Search Field Design'}
      onCancel={rest.onCancel}
    >
      <Property.Body className={styles.dialogBodyStyles}>
        <Property.Accordion title={'Border'} className={styles.borderProperty}>
          <BorderStyleCheckBox
            borderStyle={borderStyle}
            handleBorderStyle={handleBorderStyle}
          />
          <Property.Color
            title={'Color'}
            clearable={true}
            value={borderColor}
            onChange={handleBorderColor}
          />
          <Property name="Stroke Width" className={styles.borderStyleProperty}>
            <Property.Slider
              min={0}
              max={10}
              step={1}
              input
              value={borderWidth}
              onChange={handleBorderWidth}
            />
          </Property>
          <Property name="Corner Radius" className={styles.borderStyleProperty}>
            <Property.BorderRadius
              value={borderRadius}
              onChange={handleBorderRadius}
            />
          </Property>
        </Property.Accordion>
        <Property.Accordion title={`Input & Background`} className={styles.borderProperty}>
          <InputStyleCustomization
            id={id}
            editorSession={editor}
            properties={deviceProperties}
            handlePropertyChange={handlePropertyChange}
          />
        </Property.Accordion>
        <Property.Accordion title={'Button Style'} className={styles.borderProperty}>
          <SearchButtonCustomization
            id={id}
            editorSession={editor}
            properties={deviceProperties}
            handlePropertyChange={handlePropertyChange}
          />
        </Property.Accordion>
      </Property.Body>
    </Property.Dialog >
  )
}

const BorderStyleCheckBox = React.memo(({ borderStyle, handleBorderStyle}) => {

  return (
    <div key={`border_type`} className={styles.borderTypeContainer}>
      <Form.Check
        inline
        className={styles.checkBox}
        checked={borderStyle == "none"}
        label="none"
        onMouseDown={() => { handleBorderStyle('none') }}
        name="none"
        type='radio'
      />
      <Form.Check
        inline
        checked={borderStyle == "all"}
        className={styles.checkBox}
        label="all"
        name="all"
        onMouseDown={() => { handleBorderStyle('all') }}
        type="radio"
      />
      <Form.Check
        inline
        checked={borderStyle == "bottom"}
        className={styles.checkBox}
        label="bottom"
        name="bottom"
        onMouseDown={() => { handleBorderStyle('bottom') }}
        type="radio"
      />
    </div>
  )
})

const InputStyleCustomization = ({ id, editorSession, properties, handlePropertyChange }) => {
  const dispatch = useDispatch();

  const fontFamily = _.get(properties, 'font.fontFamily', 'Montserrat,sans-serif');
  const fontSize = _.get(properties, 'font.fontSize') || 16;
  const inputTextColor = _.get(properties, 'inputTextColor');
  const placeholderColor = _.get(properties, 'placeholderColor');
  const backgroundColor = _.get(properties, 'background');
  const placeholderText = _.get(properties, 'placeholder') || "";

  const handlePlaceholderTextChange = (event) => {
    var request = _.cloneDeep(properties);
    _.set(request, 'placeholder', event.target.value);
    handlePropertyChange(request);
  }

  const handleBackgroundChange = (color) => {
    var request = _.cloneDeep(properties);
    _.set(request, 'background', color);
    handlePropertyChange(request);
  }

  const handleTextColorChange = (color) => {
    var request = _.cloneDeep(properties);
    _.set(request, 'inputTextColor', color);
    handlePropertyChange(request);
  }

  const handlePlaceholderColorChange = (color) => {
    var request = _.cloneDeep(properties);
    _.set(request, 'placeholderColor', color);
    handlePropertyChange(request);
  }

  const handleFontFamily = (font) => {
    var fontId = _.get(font, 'font.id');
    var familyValue = _.get(font, 'font.fontFamily');
    var request = _.cloneDeep(properties);
    _.set(request, 'font.fontFamily', familyValue);
    handlePropertyChange(request);

    if (font) {
      dispatch(associateFontsWithWigdet(editorSession, id, [fontId]));
    }
  }

  const handlePadding = (value) => {
    var request = _.cloneDeep(properties);
    _.set(request, 'padding', value);
    handlePropertyChange(request);
  }

  const handleIconColorChange = (color) => {
    var request = _.cloneDeep(properties);
    _.set(request, 'searchIconColor', color);
    handlePropertyChange(request);
  }

  const handleFontSizeChange = (size) => {
    var request = _.cloneDeep(properties);
    _.set(request, 'font.fontSize', size);
    handlePropertyChange(request);
  }

  return (
    <>
      <Property className={styles.placeholderStyle} name='Placeholder text'>
        <Property.Input
          value={placeholderText}
          onChange={handlePlaceholderTextChange}
          placeholder="Search Placeholder"
        // error={error} 
        />
      </Property>
      <Font.Family
        value={fontFamily}
        onChange={handleFontFamily}
        className={styles.buttonStyleProperty}
      />
      <Font.Size
        value={fontSize}
        min={6}
        max={30}
        onChange={handleFontSizeChange}
        className={styles.buttonStyleProperty}
      />
      <Property.Color
        title={'Placeholder color'}
        value={placeholderColor}
        onChange={handlePlaceholderColorChange}
      />
      <Property.Color
        title={'Input Text Color'}
        clearable={true}
        value={inputTextColor}
        onChange={handleTextColorChange}
      />
      <Property.Color
        title={'Background Color'}
        gradient={true}
        clearable={true}
        value={backgroundColor}
        onChange={handleBackgroundChange}
      />
      <Property name="Left & Right Padding" className={styles.animationDurationProperty}>
        <Property.Slider
          input
          min={5}
          className={styles.customSlider}
          max={30}
          step={1}
          value={_.get(properties, 'padding', null)}
          onChange={handlePadding}
        />
      </Property>
      <Property.Color
        title={'Search Icon Color'}
        value={_.get(properties, 'searchIconColor', null)}
        onChange={handleIconColorChange}
      />
    </>
  )
}

const SearchButtonCustomization = ({ id, editorSession, properties, handlePropertyChange }) => {

  const buttonStyles = _.get(properties, 'buttonStyles', {});

  const [state, setState] = useState({
    activeTab: 'regular'
  })

  const regularShadowProps = _.get(buttonStyles, `regular.shadow`, {});
  const hoverShadowProps = _.get(buttonStyles, `hover.shadow`, {});

  const handleTabChange = (key) => {
    setState(() => {
      return {
        ...state,
        activeTab: key
      }
    })
  }

  const handleShadowChange = (field, value) => {
    var request = {
      ...properties,
      buttonStyles: {
        ...buttonStyles,
        [state.activeTab]: {
          ...buttonStyles[state.activeTab],
          shadow: {
            ...buttonStyles[state.activeTab].shadow,
            [field]: value
          }
        }
      }
    }
    handlePropertyChange(request);
  }

  return (
    <Property name="Button Design" className={styles.buttonDesignProperty}>
      <Tabs activeKey={state.activeTab}
        onSelect={handleTabChange}
        className={styles.buttonStyleTabs}>
        <Tab eventKey="regular" title="Regular" className={styles.buttonStyleTab}>
          <FontEditor
            editorSession={editorSession}
            id={id}
            properties={properties}
            active={'regular'}
          />
          <DropShadow id={id} properties={regularShadowProps} handleShadowChange={handleShadowChange} />
        </Tab>
        <Tab eventKey="hover" title="Hover" className={styles.buttonStyleTab}>
          <FontEditor
            editorSession={editorSession}
            id={id}
            properties={properties}
            active={'hover'}
          />
          <DropShadow id={id} properties={hoverShadowProps} handleShadowChange={handleShadowChange} />
        </Tab>
      </Tabs>
    </Property>
  )
}


const FontEditor = ({ editorSession, id, properties, active }) => {
  const dispatch = useDispatch();

  const breakpoint = useSelector(state => _.get(state, `${editorSession}.present.breakpoint`));
  const buttonStyles = _.get(properties, `buttonStyles`, {});

  const deviceProperties = _.get(buttonStyles, `${active}`, {});
  const border = _.get(deviceProperties, 'border', {});
  const borderWidth = _.get(border, 'width', 0);
  const borderRadius = _.get(border, 'radius', {
    topLeft: 0,
    topRight: 0,
    bottomLeft: 0,
    bottomRight: 0
  });

  const fontFamily = _.get(deviceProperties, 'font.fontFamily', 'Montserrat,sans-serif');
  const fontSize = _.get(deviceProperties, 'font.fontSize') || 16;
  const lineSpacing = _.get(deviceProperties, 'font.lineSpacing') || 1.2;
  const letterSpacing = _.get(deviceProperties, 'font.letterSpacing') || 0;
  const fontStyle = _.get(deviceProperties, 'font.style', {});

  const handlePropertyChange = (value) => {
    dispatch(updateWidgetProperty(editorSession, id, breakpoint, value));
  }

  // To Do Fix
  const [borderColor, setBorderColor] = useState(_.get(border, 'color', null));
  const [textColor, setTextColor] = useState(_.get(deviceProperties, 'font.color', null));
  const [backgroundColor, setBackgroundColor] = useState(_.get(deviceProperties, 'background', null));

  const handleBorderColor = (value) => {
    setBorderColor(value);
  };
  const handleTextColor = (value) => {
    setTextColor(value);
  };
  const handleBackgroundColor = (value) => {
    setBackgroundColor(value);
  };

  useEffect(() => {
    if (!_.isEqual(borderColor, _.get(border, `color`))) {
      handleBorderColorChange(borderColor);
      return
    }
    if (!_.isEqual(textColor, _.get(deviceProperties, 'font.color'))) {
      handleTextColorChange(textColor)
      return
    }
    if (!_.isEqual(backgroundColor, _.get(deviceProperties, 'background'))) {
      handleBackgroundColorChange(backgroundColor)
      return
    }

  }, [borderColor, backgroundColor, textColor]);

  const handleTextColorChange = React.useCallback((color) => {
    var request = {
      ...properties,
      buttonStyles: {
        ...buttonStyles,
        [active]: {
          ...buttonStyles[active],
          font: {
            ...deviceProperties.font,
            color: color
          }
        }
      }
    }

    handlePropertyChange(request);
  }, [properties]);

  const handleBackgroundColorChange = React.useCallback((color) => {
    var request = {
      ...properties,
      buttonStyles: {
        ...buttonStyles,
        [active]: {
          ...buttonStyles[active],
          background: color
        }
      }
    }

    handlePropertyChange(request);
  }, [properties]);

  const handleBorderColorChange = (color) => {
    var request = {
      ...properties,
      buttonStyles: {
        ...buttonStyles,
        [active]: {
          ...deviceProperties,
          border: {
            ...border,
            color: color
          }
        }
      }
    }

    handlePropertyChange(request);
  }

  const handleBorderWidthChange = (value) => {
    var request = {
      ...properties,
      buttonStyles: {
        ...buttonStyles,
        [active]: {
          ...deviceProperties,
          border: {
            ...border,
            width: value
          }
        }
      }
    }

    handlePropertyChange(request);
  }

  const handleBorderRadiusChange = (radius) => {
    var request = {
      ...properties,
      buttonStyles: {
        ...buttonStyles,
        [active]: {
          ...deviceProperties,
          border: {
            ...border,
            radius: radius
          }
        }
      }
    }

    handlePropertyChange(request);
  }

  const handleFontFamily = (value) => {
    var font = _.get(value, 'font.id');
    var family = _.get(value, 'font.fontFamily');
    if (!family) {
      return;
    }

    var request = {
      ...properties,
      buttonStyles: {
        ...buttonStyles,
        [active]: {
          ...deviceProperties,
          font: {
            ...deviceProperties.font,
            fontFamily: family
          }
        }
      }
    }

    handlePropertyChange(request);
    if (font) {
      dispatch(associateFontsWithWigdet(editorSession, id, [font]));
    }
  }

  const handleSizeChange = (value) => {
    var request = {
      ...properties,
      buttonStyles: {
        ...buttonStyles,
        [active]: {
          ...deviceProperties,
          font: {
            ...deviceProperties.font,
            fontSize: value
          }
        }
      }
    }

    handlePropertyChange(request);
  }

  const handleLineSpacingChange = (value) => {
    var request = {
      ...properties,
      buttonStyles: {
        ...buttonStyles,
        [active]: {
          ...deviceProperties,
          font: {
            ...deviceProperties.font,
            lineSpacing: value
          }
        }
      }
    }

    handlePropertyChange(request);
  }

  const handleLetterSpacingChange = (value) => {
    var request = {
      ...properties,
      buttonStyles: {
        ...buttonStyles,
        [active]: {
          ...deviceProperties,
          font: {
            ...deviceProperties.font,
            letterSpacing: value
          }
        }
      }
    }

    handlePropertyChange(request);
  }

  const handleFontStyleChange = (style) => {
    var request = {
      ...properties,
      buttonStyles: {
        ...buttonStyles,
        [active]: {
          ...deviceProperties,
          font: {
            ...deviceProperties.font,
            style: {
              ...fontStyle,
              [style]: !fontStyle[style]
            }
          }
        }
      }
    }

    handlePropertyChange(request);
  }

  return (
    <>
      <Property.Accordion title={'Color'} className={styles.colorProperty}>
        <Property.Color title={'Text'} value={_.get(deviceProperties, 'font.color', null)} onChange={handleTextColorChange} />
        <Property.Color title={'Background'} gradient={true} clearable={true} value={_.get(deviceProperties, 'background', null)} onChange={handleBackgroundColorChange} />
      </Property.Accordion>

      <Property.Accordion title={'Border'} className={styles.borderProperty}>
        <Property.Color title={'Color'} clearable={true} value={borderColor} onChange={handleBorderColor} />
        {active === 'regular' &&
          <>
            <Property name="Stroke Width" className={styles.buttonStyleProperty}>
              <Property.Slider
                min={0}
                max={10}
                step={1}
                input
                value={borderWidth}
                onChange={handleBorderWidthChange}
              />
            </Property>
            <Property name="Corner Radius" className={styles.buttonStyleProperty}>
              <Property.BorderRadius
                value={borderRadius}
                onChange={handleBorderRadiusChange}
              />
            </Property>
          </>
        }

      </Property.Accordion>
      {active === 'regular' &&
        <Property.Accordion title={'Text'} className={styles.textProperty}>
          <Font.Family
            value={fontFamily}
            onChange={handleFontFamily}
            className={styles.buttonStyleProperty}
          />
          <Font.Size
            value={fontSize}
            max={20}
            onChange={handleSizeChange}
            className={styles.buttonStyleProperty}
          />

          <Property className={styles.buttonStyleProperty}>
            <div className={styles.fontActions}>
              <Property.Action className={cx(styles.iconButton, { [styles.active]: _.get(fontStyle, 'bold', false) })}
                onClick={handleFontStyleChange.bind(this, 'bold')}
              >
                <i className={'cleverstory-bold'} />
              </Property.Action>
              <Property.Action className={cx(styles.iconButton, { [styles.active]: _.get(fontStyle, 'italic', false) })}
                onClick={handleFontStyleChange.bind(this, 'italic')}
              >
                <i className={'cleverstory-italics'} />
              </Property.Action>
              <Property.Action className={cx(styles.iconButton, { [styles.active]: _.get(fontStyle, 'underline', false) })}
                onClick={handleFontStyleChange.bind(this, 'underline')}
              >
                <i className={'cleverstory-underline'} />
              </Property.Action>
            </div>
          </Property>
          <Property name="Line Spacing" className={styles.lineSpacingProperty}>
            <Property.Slider
              input
              min={0.5}
              max={3.0}
              step={0.1}
              value={lineSpacing}
              onChange={handleLineSpacingChange}
            />
          </Property>
          <Property name="Character Spacing" className={styles.characterSpacingProperty}>
            <Property.Slider
              input
              min={-0.1}
              max={0.7}
              step={0.05}
              value={letterSpacing}
              onChange={handleLetterSpacingChange}>
            </Property.Slider>
          </Property>
        </Property.Accordion>
      }
    </>
  )
}

SearchWidget.actions = (editor, breakpoint, props) => {

  return (
    <>
      {breakpoint === 'desktop' &&
        <SearchWidgetDesktopAction editorSession={editor} breakpoint={breakpoint} {...props} />
      }
    </>
  );
}

SearchWidget.sizeConfig = async (widget, mediaCache) => {
  const url = _.get(widget, 'properties.desktop.background.media.url', '');
  if (!url) {
    return {
      size: 0,
      type: 'image'
    };
  }
  return {
    size:  await getMediaInfo(`${IMAGE_CDN_ENDPOINT}/${url}`, mediaCache) || 0, // MB
    type: 'image'
  };
}

SearchWidget.moveableConfig = (editorSession, widget, breakpoint) => {
  const contentCard = getParentContainerOfType(editorSession, widget.uid, CONTENT_FILTER);
  var bounds = null;
  if (contentCard) {
    bounds = calculateBoundsForElement(contentCard);
  }

  return {
    draggable: false,
    resizable: true,
    rotatable: false,
    selectable: true,
    cloneable: false,
    deletable: false,
    renderDirections: ["e", "w"],
    bounds: bounds,
    onReparent: (widget, parent) => {
      return false
    },
    onResize: (widget, layout, breakpoint) => {
      const frame = new Frame(layout);
      frame.set('height', 'auto');
      return frame.get();
    },
    onDrag: (layout, widget) => {
      const frame = new Frame(layout);
      
      const parentElement = document.querySelector(`#${editorSession} .editor_widget_scroll_container #search_container_column_${widget}`);

      if(parentElement) {
        parentElement.style.removeProperty('width');
      }

      return frame.get();
    }
  }
}

/**
 * Generate  column Styles
 * 
*/
function getColumnStyles(properties, horizontalColumn, breakpoint, alignment) {
  var containerStyles = {};

  if (!properties) {
    return containerStyles;
  }

  const paddingLeft = _.get(alignment, 'paddingLeft', 0);
  const paddingRight = _.get(alignment, 'paddingRight', 0);

  const columnAlignment = _.get(properties, `horizontalAlignment`, {
    position: 'center',
    margin: 0,
  });

  var alignmentPosition = _.get(columnAlignment, 'position', 'center');
  var alignmnetMargin = _.get(columnAlignment, 'margin', 0);

  //alignment
  containerStyles['justifyContent'] = alignmentPosition;

  if (!horizontalColumn) {

    if(breakpoint === 'mobile' && _.get(properties, 'layout.width') !== '100%') {
      if(paddingLeft) {
        containerStyles['paddingLeft'] = paddingLeft;
      }
      if(paddingRight) {
        containerStyles['paddingRight'] = paddingRight;
      }
    }

    return containerStyles;
  }

  if (alignmentPosition === 'left') {
    containerStyles = {
      ...containerStyles,
      justifyContent: 'flex-start',
      paddingLeft: `${alignmnetMargin}px`
    }
  }
  else if (alignmentPosition === 'right') {
    containerStyles = {
      ...containerStyles,
      justifyContent: 'flex-end',
      paddingRight: `${alignmnetMargin}px`
    }
  }

  return containerStyles;
}

export function getColorStyles(value) {
  if (!value) {
    return null;
  }

  const colorRgb = value;

  var color = 'inherit';
  if (colorRgb) {
    color = `rgba(${colorRgb.r}, ${colorRgb.g}, ${colorRgb.b}, ${colorRgb.a})`;
  }

  var styles = {
    color: color
  };

  return styles;
}

export function getBorderStyles(value) {
  if (!value) {
    return null;
  }

  var styles = {};
  const topLeft = _.get(value, 'radius.topLeft', null);
  const topRight = _.get(value, 'radius.topRight', null);
  const bottomLeft = _.get(value, 'radius.bottomLeft', null);
  const bottomRight = _.get(value, 'radius.bottomRight', null);

  if (topLeft) {
    styles['borderTopLeftRadius'] = parseInt(topLeft) + 'px';
  }

  if (topRight) {
    styles['borderTopRightRadius'] = parseInt(topRight) + 'px';
  }

  if (bottomLeft) {
    styles['borderBottomLeftRadius'] = parseInt(bottomLeft) + 'px';
  }

  if (bottomRight) {
    styles['borderBottomRightRadius'] = parseInt(bottomRight) + 'px';
  }

  const colorRgb = _.get(value, 'color');

  var color = 'transparent';
  if (colorRgb) {
    color = `rgba(${colorRgb.r}, ${colorRgb.g}, ${colorRgb.b}, ${colorRgb.a})`;
  }

  styles['borderColor'] = color;

  const borderWidthString = _.get(value, 'width', null);
  var borderWidget = parseInt(borderWidthString) + 'px';

  styles['borderWidth'] = borderWidget;

  return styles;
}

export { SearchWidget };